// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import { Modal, Spinner } from '@loa/ui-components';
import { ProjectionAssumptionsEditor } from './ProjectionAssumptionsEditor';
import {
  EMPTY_ASSUMPTIONS,
  projectionAssumptionsApi,
  type EffectiveAssumptions,
  type ProjectionAssumptions,
} from '../../api/projectionAssumptionsApi';

interface Props {
  profileId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Dialog to override the projection assumptions at the RELATIONSHIP level. Opened
 * from the relationship actions ("…") menu. Shows the resolved (inherited) values
 * as hints; "Clear" saves an all-null override so every key re-inherits from the
 * advisor/brokerage/global levels. See docs/specs/layered-projection-assumptions-prd.md.
 */
export function RelationshipAssumptionsDialog({ profileId, open, onClose }: Props) {
  const [override, setOverride] = useState<ProjectionAssumptions | null>(null);
  const [inherited, setInherited] = useState<EffectiveAssumptions | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      projectionAssumptionsApi.getRelationship(profileId),
      projectionAssumptionsApi.getRelationshipInherited(profileId),
    ])
      .then(([o, e]) => {
        setOverride(o);
        setInherited(e);
      })
      .finally(() => setLoading(false));
  }, [profileId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  return (
    <Modal open onClose={onClose} size="large">
      <Modal.Header icon="chart" title="Projection assumptions (this relationship)" onClose={onClose} />
      <Modal.Content>
        <div data-testid="relationship-assumptions-dialog">
          {loading && <Spinner />}
          {override && (
            <ProjectionAssumptionsEditor
              override={override}
              effective={inherited}
              onSave={(body) => projectionAssumptionsApi.saveRelationship(profileId, body).then(() => {
                load();
              })}
              onClear={() => projectionAssumptionsApi.saveRelationship(profileId, EMPTY_ASSUMPTIONS).then(() => {
                load();
              })}
              testId="relationship-assumptions-editor"
            />
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
}
